// =============================================================================
// useFangateMoney — locale-aware money + relative-time formatters
//
// Fangate stores prices as integer cents (docs example: `"price": 4400` = €44.00).
// The /api/dashboard/summary call returns the creator's currency code
// (EUR/USD/GBP/CHF…). This composable maps that code to a sensible LOCALE
// (symbol placement is locale-driven, not currency-driven) and returns ready
// formatters that all Fangate children share.
//
//   const { formatMoney, formatBalance, formatRelTime } = useFangateMoney(currencyRef);
//
// Symbol placement rules:
//   EUR → de-DE → "44,00 €"     (symbol AFTER,  comma decimal)
//   USD → en-US → "$ 44.00"     (symbol BEFORE, with NBSP gap for readability)
//   GBP → en-GB → "£ 44.00"     (symbol BEFORE)
//   CHF → de-CH → "CHF 44.00"
// Unknown currency → en-US fallback.
// =============================================================================

import { computed } from 'vue';

const localeForCurrency = code => ({
  EUR: 'de-DE',
  USD: 'en-US',
  GBP: 'en-GB',
  CHF: 'de-CH',
  CAD: 'en-CA',
  AUD: 'en-AU',
}[code] || 'en-US');

export default function useFangateMoney(currencyRef) {
  const moneyFormatter = computed(() => {
    const code = (currencyRef.value || 'EUR').toUpperCase();
    try {
      return new Intl.NumberFormat(localeForCurrency(code), {
        style: 'currency',
        currency: code,
      });
    } catch (_) {
      // Bad currency code → safe EUR fallback.
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    }
  });

  // Insert a non-breaking space between $/£/¥ and the digits.
  // en-* locales render "$44.00" with no gap — we want "$ 44.00".
  // EUR / CHF already place the symbol after with a space.
  const withSymbolGap = s => s.replace(/([$£¥])(\d)/, '$1 $2');

  // Money in the UI: 0 collapses to "Free" (used on product cards / link rows).
  const formatMoney = n => {
    if (n == null || isNaN(n)) return '—';
    if (n === 0) return 'Free';
    return withSymbolGap(moneyFormatter.value.format(Number(n)));
  };

  // Balance/wallet amounts: skip the "Free" short-circuit. 0 € pending is
  // real info, not "free". Used for Sales-tab KPIs.
  const formatBalance = n => {
    if (n == null || isNaN(n)) return '—';
    return withSymbolGap(moneyFormatter.value.format(Number(n)));
  };

  const formatRelTime = ts => {
    if (!ts) return '';
    const diff = Math.floor(Date.now() / 1000 - Number(ts));
    if (diff < 60)    return 'just now';
    if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  // Standalone currency symbol — for input prefixes / placeholders where we
  // don't want a digit-bearing format.
  //   EUR → "€"   USD → "$"   GBP → "£"   CHF → "CHF"
  // Extracted via Intl.formatToParts so we always match what formatMoney emits
  // (no separate symbol table to keep in sync).
  const currencySymbol = computed(() => {
    const code = (currencyRef.value || 'EUR').toUpperCase();
    try {
      const part = new Intl.NumberFormat(localeForCurrency(code), {
        style: 'currency', currency: code, maximumFractionDigits: 0,
      }).formatToParts(0).find(p => p.type === 'currency');
      return part?.value || code;
    } catch (_) { return '€'; }
  });

  return {
    moneyFormatter,
    formatMoney,
    formatBalance,
    formatRelTime,
    currencySymbol,
  };
}
